import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import SearchResults from "@/pages/SearchResults";
import ClauseDetail from "@/pages/ClauseDetail";
import ContractUpload from "@/pages/ContractUpload";
import FlowdownAnalysis from "@/pages/FlowdownAnalysis";
import ContractDetail from "@/pages/ContractDetail";
import ContractComparison from "@/pages/ContractComparison";
import AgiloftIntegration from "@/pages/AgiloftIntegration";
import BatchExport from "@/pages/BatchExport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function apiFetch(url, options = {}) {
  const token = localStorage.getItem("session_token");
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
          });

          if (response.ok) {
            const user = await response.json();
            navigate("/dashboard", { state: { user }, replace: true });
          } else {
            navigate("/", { replace: true });
          }
        } catch (error) {
          console.error("Auth error:", error);
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center app-background">
      <div className="text-center">
        <div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-600 font-medium">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user was passed from AuthCallback, use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem("session_token");
        if (!token) {
          setIsAuthenticated(false);
          navigate("/");
          return;
        }
        const response = await fetch(`${API}/auth/me`, {
          headers: { "Authorization": `Bearer ${token}` },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          navigate("/");
        }
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/");
      }
    };

    checkAuth();
  }, [navigate, location.state]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center app-background">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Clone children and pass user prop
  return typeof children === 'function' ? children({ user }) : children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL fragment - must be synchronous during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/clause/:clauseId" element={<ClauseDetail />} />
      <Route
        path="/upload"
        element={
          <ProtectedRoute>
            {({ user }) => <ContractUpload user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/flowdown"
        element={
          <ProtectedRoute>
            {({ user }) => <FlowdownAnalysis user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/contract/:contractId"
        element={
          <ProtectedRoute>
            {({ user }) => <ContractDetail user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/compare"
        element={
          <ProtectedRoute>
            {({ user }) => <ContractComparison user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/agiloft"
        element={
          <ProtectedRoute>
            {({ user }) => <AgiloftIntegration user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/export"
        element={
          <ProtectedRoute>
            {({ user }) => <BatchExport user={user} />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
